(ns client-portal.client.user-management.roles.views
  (:require [client-portal.client.layout :as l]
            [client-portal.client.user-management.components :as um.c]
            [client-portal.client.user-management.data :as um.d]
            [client-portal.client.user-management.roles.data :as roles.d]
            [client-portal.client.voice-insights.empty-state :as empty-state]
            [clojure.string :as s]
            [shared.core.amelia-light.components.components :as alc]
            [shared.core.reactive :as re]
            [shared.core.style :as style]
            [shared.core.util :as util]))

(defn- selected-application [applications application-id]
  (or (some #(when (= application-id (:id %)) %) applications)
      (some #(when (= "client-portal" (:external-id %)) %) applications)
      (first applications)))

(defn roles-table [{:keys [application-id]}]
  (let [roles @(re/subscribe [:user-management/roles-])
        permissions-by-id (um.d/by-id :id @(re/subscribe [:user-management/permissions-]))
        user-counts @(re/subscribe [:user-management/role-user-counts-])
        compact? (not @(re/subscribe [:core/breakpoint-min?- :lg]))
        application-roles (->> (um.d/rows roles)
                               (filter #(= application-id (:application-id %)))
                               (sort-by :label))]
    (cond
      (= roles :error)
      [empty-state/no-data-card {:text "Roles couldn't be loaded. Reload the page to try again."
                                 :material-icon-type :error-outline}]

      (util/nil-or-loading? roles)
      [empty-state/loading-card {:label "Loading roles…"}]

      (empty? application-roles)
      [empty-state/no-data-card {:text "This application has no roles yet."
                                 :material-icon-type :badge}]

      :else
      [:div {:style {:max-width um.c/max-content-width}}
       [alc/table {:no-data-text "No roles"
                   :row-style {:align-items :center}
                   :flex-vals (if compact?
                                [1 "0 0 72px"]
                                [1 2.4 "0 0 104px"])
                   :headers (if compact?
                              ["Role" "People"]
                              ["Role" "Permissions" "People Here"])
                   :rows (for [{:keys [id label permission-ids internal] :as role} application-roles
                               :let [scopes (->> permission-ids
                                                 (keep #(get-in permissions-by-id [% :scope]))
                                                 (sort))
                                     role-name [:span {:style {:align-items :center
                                                               :display :flex
                                                               :flex-wrap :wrap
                                                               :gap 6
                                                               :overflow-wrap :anywhere}}
                                                label
                                                (when internal
                                                  [um.c/internal-badge])]
                                     role-scopes (if (empty? scopes)
                                                   [um.c/secondary-text {:style {:font-size 12}} "No permissions"]
                                                   (into [:span {:style {:display :flex
                                                                         :flex-wrap :wrap
                                                                         :gap 4}}]
                                                         (for [scope scopes]
                                                           [:code {:style {:background-color (style/alc-color "Background/secondary")
                                                                           :border-radius 4
                                                                           :color (style/alc-color "Text/primary")
                                                                           :font-size 12
                                                                           :max-width "100%"
                                                                           :overflow-wrap :anywhere
                                                                           :padding "1px 6px"}}
                                                            scope])))
                                     people {:label (str (get user-counts id 0))
                                             :style {:font-variant-numeric "tabular-nums"}}]]
                           {:cells (if compact?
                                     [{:label [:div {:style {:display :flex
                                                             :flex-direction :column
                                                             :gap 8
                                                             :min-width 0}}
                                               role-name
                                               role-scopes]}
                                      people]
                                     [{:label role-name}
                                      {:label role-scopes}
                                      people])
                            :on-click #(re/dispatch [:user-management/edit-role role application-id])})}]])))

(defn roles-section []
  (let [applications (um.d/rows @(re/subscribe [:user-management/applications-]))
        {:keys [id label]} (selected-application applications @(re/subscribe [:user-management/application-filter-]))]
    [l/standard-content-container {:title "Roles"
                                   :header-contents [um.c/toolbar
                                                     (when (< 1 (count applications))
                                                       [alc/up-popover-button-dropdown {:placeholder "Choose an Application"
                                                                                        :fixed-icon-type :LayersIcon
                                                                                        :align :start
                                                                                        :options (mapv (fn [application]
                                                                                                         {:text (:label application)
                                                                                                          :value (:id application)
                                                                                                          :selected? (= id (:id application))})
                                                                                                       applications)
                                                                                        :on-change #(re/dispatch [:user-management/set-filter :application-id %])}])
                                                     [um.c/secondary-text {}
                                                      "Features check roles by name. A role's permissions decide which data it can reach."]
                                                     [um.c/spacer]
                                                     [alc/up-call-to-action-button {:icon-type :AddIcon
                                                                                    :disabled (nil? id)
                                                                                    :on-click #(re/dispatch [:user-management/edit-role nil id])}
                                                      "New Role"]]
                                   :sticky-header? true
                                   :container-style {:flex 1
                                                     :min-height :auto}}
     (when id
       [roles-table {:application-id id
                     :application-label label}])]))

(defmethod alc/dialog-view
  :user-management/role-dialog [_ _]
  (let [{:keys [id application-id label original-label internal]} @(re/subscribe [:user-management/role-form-])
        application (get @(re/subscribe [:user-management/applications-by-id-]) application-id)
        permissions (um.d/rows @(re/subscribe [:user-management/permissions-]))
        renamed? (and id (not= (some-> label (s/trim)) original-label))
        external-holders (when (and id internal)
                           (->> (um.d/rows @(re/subscribe [:user-management/users-]))
                                (filter #(and (some #{id} (:application-role-ids %))
                                              (not (util/soundhound-email? (:email-address %)))))
                                (count)))]
    [alc/dialog {:title (if id "Edit Role" "New Role")
                 :description (str "For " (:label application))
                 :footer [:<>
                          [alc/up-call-to-action-button {:loading? @(re/subscribe [:user-management/saving-role?-])
                                                         :disabled (s/blank? label)
                                                         :on-click #(re/dispatch [:user-management/save-role])}
                           (if id "Save Changes" "Create Role")]
                          [alc/up-button {:on-click #(re/dispatch [:dialog/hide])}
                           "Cancel"]]}
     [:div {:style {:display :flex
                    :flex-direction :column
                    :gap 18
                    :max-width "100%"
                    :width 520}}
      [:div {:style {:display :flex
                     :flex-direction :column
                     :gap 6}}
       [:label {:html-for "um-role-name"
                :style {:color (style/alc-color "Text/primary_header")
                        :font-size 14
                        :font-weight 500}}
        "Name"]
       [alc/up-input {:id "um-role-name"
                      :form-path roles.d/form-path
                      :form-name :label
                      :auto-focus true
                      :placeholder "reports-user"}]
       (when renamed?
         [um.c/error-text (str "Features look for \"" original-label "\" by name. Renaming it can take access away from "
                               "everyone who has this role.")])]
      [:div {:style {:display :flex
                     :flex-direction :column
                     :gap 6}}
       [:div {:style {:align-items :center
                      :display :flex
                      :gap 10}}
        [alc/up-toggle {:id "um-role-internal"
                        :form-path roles.d/form-path
                        :form-name :internal}]
        [:label {:html-for "um-role-internal"
                 :style {:color (style/alc-color "Text/primary_header")
                         :cursor :pointer
                         :font-size 14
                         :font-weight 500}}
         "Internal Only"]]
       [um.c/secondary-text {} "Only people with a @soundhound.com email can be given this role."]
       (when (pos? external-holders)
         [um.c/error-text (str (um.c/count-text external-holders "person" "people")
                               " outside SoundHound already "
                               (if (= 1 external-holders) "has" "have")
                               " this role here. They keep it until someone edits their profile, which won't save until it's removed.")])]
      [:div {:style {:display :flex
                     :flex-direction :column
                     :gap 6}}
       [:span {:id "um-role-permissions"
               :style {:color (style/alc-color "Text/primary_header")
                       :font-size 14
                       :font-weight 500}}
        "Permissions"]
       [alc/up-multiple-dropdown {:form-path roles.d/form-path
                                  :form-name :permission-ids
                                  :aria-labelledby "um-role-permissions"
                                  :placeholder (if permissions "Choose permissions" "Loading permissions…")
                                  :options (mapv (fn [{:keys [id scope]}]
                                                   {:text scope
                                                    :value id})
                                                 permissions)}]
       [um.c/secondary-text {} "Saving applies to everyone with this role the next time they load a page."]]]]))

(defmethod alc/dialog-view
  :user-management/delete-permission [_ {:keys [id scope role-labels other-role-count]}]
  (let [holders (cond-> (vec role-labels)
                        (pos? other-role-count) (conj (um.c/count-text other-role-count
                                                                       "role in another application"
                                                                       "roles in other applications")))]
    [um.c/confirm-dialog {:title "Delete Permission"
                          :message (str (if (empty? holders)
                                          (str "No role uses " scope ".")
                                          (str "Deleting " scope " takes it away from " (um.c/join-words holders) "."))
                                        " Any endpoint that requires it will refuse everyone afterwards.")
                          :confirm-text "Delete Permission"
                          :busy? @(re/subscribe [:user-management/permission-request-active?-])
                          :on-confirm #(re/dispatch [:user-management/delete-permission {:id id
                                                                                         :scope scope}])}]))

(defn permissions-table []
  (let [permissions @(re/subscribe [:user-management/permissions-])
        roles (um.d/rows @(re/subscribe [:user-management/roles-]))
        applications-by-id @(re/subscribe [:user-management/applications-by-id-])
        compact? (not @(re/subscribe [:core/breakpoint-min?- :lg]))
        holders (fn [permission-id]
                  (filter #(some #{permission-id} (:permission-ids %)) roles))
        role-labels (fn [permission-id]
                      (->> (holders permission-id)
                           (filter #(contains? applications-by-id (:application-id %)))
                           (map (fn [{:keys [label application-id]}]
                                  (let [application (get applications-by-id application-id)]
                                    (if (= "client-portal" (:external-id application))
                                      label
                                      (str label " (" (:label application) " app)")))))
                           (sort)))]
    (cond
      (= permissions :error)
      [empty-state/no-data-card {:text "Permissions couldn't be loaded. Reload the page to try again."
                                 :material-icon-type :error-outline}]

      (util/nil-or-loading? permissions)
      [empty-state/loading-card {:label "Loading permissions…"}]

      :else
      [:div {:style {:max-width um.c/max-content-width}}
       [alc/table {:no-data-text "No permissions yet"
                   :row-style {:align-items :center}
                   :flex-vals (if compact?
                                [1 "0 0 48px"]
                                [1 2 "0 0 56px"])
                   :headers (if compact?
                              ["Scope" ""]
                              ["Scope" "Used By" ""])
                   :rows (for [{:keys [id scope]} permissions
                               :let [labels (role-labels id)
                                     scope-code [:code {:style {:font-size 13
                                                                :overflow-wrap :anywhere}}
                                                 scope]
                                     used-by (if (empty? labels)
                                               [um.c/secondary-text {:style {:font-size 12}} "No roles"]
                                               [um.c/role-pills labels])
                                     delete {:label [alc/up-ghost-button {:icon-type :DeleteIcon
                                                                          :icon "sm"
                                                                          :aria-label (str "Delete " scope)
                                                                          :tooltip-content "Delete Permission"
                                                                          :on-click #(re/dispatch [:dialog/show [:user-management/delete-permission
                                                                                                                 {:id id
                                                                                                                  :scope scope
                                                                                                                  :role-labels labels
                                                                                                                  :other-role-count (- (count (holders id))
                                                                                                                                       (count labels))}]])}]
                                             :style {:padding "0 8px 0 0"}}]]
                           {:cells (if compact?
                                     [{:label [:div {:style {:display :flex
                                                             :flex-direction :column
                                                             :gap 8
                                                             :min-width 0}}
                                               scope-code
                                               used-by]}
                                      delete]
                                     [{:label scope-code}
                                      {:label used-by}
                                      delete])})}]])))

(defn permissions-section []
  (let [scope @(re/subscribe [:form/val- roles.d/permission-form-path :scope])
        busy? @(re/subscribe [:user-management/permission-request-active?-])
        add #(re/dispatch [:user-management/add-permission])]
    [l/standard-content-container {:title "Permissions"
                                   :header-contents [um.c/toolbar
                                                     [:form {:style {:display :flex
                                                                     :flex "1 1 360px"
                                                                     :gap 8
                                                                     :max-width 480}
                                                             :on-submit (fn [e]
                                                                          (.preventDefault e)
                                                                          (add))}
                                                      [:div {:style {:flex 1}}
                                                       [alc/up-input {:form-path roles.d/permission-form-path
                                                                      :form-name :scope
                                                                      :aria-label "New permission scope"
                                                                      :placeholder "New scope, like read:reports"}]]
                                                      [alc/up-button {:type "submit"
                                                                      :icon-type :AddIcon
                                                                      :loading? busy?
                                                                      :disabled (s/blank? scope)}
                                                       "Add Permission"]]
                                                     [um.c/secondary-text {:style {:flex "1 1 240px"}}
                                                      "Endpoints require scopes. Roles grant them."]]
                                   :sticky-header? true
                                   :container-style {:flex 1
                                                     :min-height :auto}}
     [permissions-table]]))
